import { t } from '@/i18n';

/**
 * TRAD-3690 — 서버 응답 enum 의 «미지값» 처리 공용 헬퍼.
 *
 * davidyoon 2026-10-11: «백엔드 데이터 하나 추가가 클라까지 영향 가는 건 유연하지 않다». 서버가 enum 에 값을 하나 더해
 * 먼저 배포되는 순간, 클라는 그 값을 모른다. 종전엔 화면마다 이 판단을 따로 했고(감사: 12개 도메인 · 12가지 방식), 빠뜨린
 * 자리에선 삼항의 암묵 else 가 모르는 값을 **아는 값으로 보이게** 했다(투표 «하락» · 전월세 «월세» · 일지 «매도» 등 —
 * docs-ops/2026-10-11-TRAD-3690-web-server-enum-unknown-audit.md §2).
 *
 * 규칙(Confluence 282329090): 모르는 값 → 라벨 «알 수 없음 / Unknown» · 중립색 · 행동 잠금 · **아는 값으로 대체 금지**.
 *
 * - `match(raw, cases)` 는 각 값 «과» UNKNOWN 을 전부 받아야 컴파일된다 — 모르는 값 갈래를 잊을 수 없다.
 * - `parse(raw)` 는 아는 값이면 그 값, 아니면 UNKNOWN(null·숫자·빈 문자열 포함). 행동 잠금은 호출부가 `parse(x) === UNKNOWN`.
 * - 서버 응답 타입은 넓게 받는다(`T | string`) — 맨 문자열(`voteType: string` 등)도 이 헬퍼를 거쳐야 분기한다.
 */
export const UNKNOWN = 'UNKNOWN' as const;
export type Unknown = typeof UNKNOWN;

export type ServerEnum<T extends string> = {
  readonly values: readonly T[];
  isKnown: (raw: unknown) => raw is T;
  parse: (raw: unknown) => T | Unknown;
  match: <R>(raw: unknown, cases: Record<T | Unknown, R>) => R;
};

export const defineServerEnum = <const T extends string>(values: readonly T[]): ServerEnum<T> => {
  // 센티널(UNKNOWN)이 문자열이라, 서버 enum 이 실제 값 'UNKNOWN' 을 가지면 «아는 값»과 «모르는 값»이 겹친다(match 의 UNKNOWN 갈래가
  // 아는 값에도 걸린다). 정의 시점에 실패시킨다 — 그런 enum 은 이 헬퍼 대신 따로 다룬다(팀장 리뷰 2026-10-11).
  if ((values as readonly string[]).includes(UNKNOWN)) {
    throw new Error(`defineServerEnum: values must not contain the sentinel '${UNKNOWN}' (known and unknown would overlap)`);
  }
  const known = new Set<string>(values);
  const isKnown = (raw: unknown): raw is T => typeof raw === 'string' && known.has(raw);
  const parse = (raw: unknown): T | Unknown => (isKnown(raw) ? raw : UNKNOWN);
  const match = <R>(raw: unknown, cases: Record<T | Unknown, R>): R => cases[parse(raw)];
  return { values, isKnown, parse, match };
};

/** 모르는 값 라벨(«알 수 없음 / Unknown») — 렌더 시점에 로케일을 읽는다. */
export const unknownLabel = (): string => t('common.unknown');

const reportedUnknowns = new Set<string>();

/**
 * 모르는 서버 값의 «원값»을 남긴다(정본 282329090 — 화면엔 «알 수 없음», 원값은 로그로). 같은 필드·값은 한 번만
 * (지도 마커처럼 한 화면에 수십 번 그려지는 자리에서 콘솔이 같은 줄로 덮이지 않게). 반환값 없음 — 화면 분기에 쓰지 않는다.
 */
export const reportUnknownServerValue = (field: string, raw: unknown): void => {
  const key = `${field}\u0000${typeof raw}:${String(raw)}`;
  if (reportedUnknowns.has(key)) return;
  reportedUnknowns.add(key);
  console.warn(`[server-enum] unknown ${field}`, raw);
};
